import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  TrendingUp, FileDown, CalendarClock, Plus, Trash2, X, Mail, Play,
  Sparkles, AlertTriangle, MessageSquareText, ArrowRight, Clock
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const NAVY = "#1a1a1a";
const SCARLET = "#ff4500";

export default function Reports() {
  const nav = useNavigate();
  const [tab, setTab] = useState("trends");
  const [days, setDays] = useState(30);
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [trends, setTrends] = useState(null);

  const [reports, setReports] = useState([]);
  const [instances, setInstances] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [editingReport, setEditingReport] = useState(null);

  const loadCore = async () => {
    const [t, r, i, m] = await Promise.all([
      api.get("/checklist-templates"),
      api.get("/reports"),
      api.get("/report-instances"),
      api.get("/meetings"),
    ]);
    setTemplates(t.data);
    setReports(r.data);
    setInstances(i.data);
    setMeetings(m.data);
  };

  const loadTrends = async () => {
    const params = { days };
    if (templateId) params.template_id = templateId;
    const r = await api.get("/trends/overview", { params });
    setTrends(r.data);
  };

  useEffect(() => { loadCore(); }, []);
  useEffect(() => { loadTrends(); /* eslint-disable-next-line */ }, [days, templateId]);

  const runNow = async (id) => {
    toast.promise(api.post(`/reports/${id}/run-now`).then(async (r) => {
      await loadCore();
      return r.data;
    }), { loading: "Generating PDF…", success: "Report generated", error: "Failed" });
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Delete this scheduled report?")) return;
    await api.delete(`/reports/${id}`);
    toast.success("Deleted");
    loadCore();
  };

  const openInstanceAsMeeting = async (inst) => {
    // Create a meeting linked to this instance and navigate
    const r = await api.post("/meetings", {
      title: `Review · ${inst.title} (${inst.label})`,
      report_instance_id: inst.id,
      scheduled_for: new Date().toISOString(),
      attendees: (inst.recipients || []).map((x) => ({ ...x, present: false })),
      agenda: [
        { title: "Trend overview & KPI review", notes: "" },
        { title: "Top failing items — root cause", notes: "" },
        { title: "Corrective actions & owners", notes: "" },
      ],
    });
    nav(`/app/close-out-meeting/${r.data.id}`);
  };

  const downloadPdf = (fileId) => {
    const token = localStorage.getItem("opscmd_token");
    window.open(`${API}/files/${fileId}?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Hero strip */}
      <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <Sparkles className="w-3 h-3 text-[#ff5f15]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Trends · Reports · Meetings</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl leading-tight tracking-tight">
              From floor data<br/><span className="text-[#ff5f15]">to leadership decisions.</span>
            </h1>
            <p className="mt-2 text-sm text-white/75 max-w-md">Auto-generate weekly, monthly, and quarterly compliance PDFs. Then open them in-app as meetings with attendees, minutes, and action logs.</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="new-report-button" onClick={() => setEditingReport({})} className="btn-pill btn-pill-primary">
              <Plus className="w-3.5 h-3.5" /> Schedule report
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          ["trends", "Live trends", TrendingUp],
          ["scheduled", `Scheduled (${reports.length})`, CalendarClock],
          ["generated", `Generated (${instances.length})`, FileDown],
          ["meetings", `Meetings (${meetings.length})`, MessageSquareText],
        ].map(([k, label, Icon]) => {
          const active = tab === k;
          return (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] uppercase tracking-wider font-bold transition-colors duration-150 ${
                active ? "bg-[#1a1a1a] text-white shadow-soft" : "bg-white text-[#1a1a1a] border border-[#1a1a1a]/15 hover:bg-[#fff1ea]"
              }`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          );
        })}
      </div>

      {tab === "trends" && (
        <TrendsView trends={trends} days={days} setDays={setDays} templateId={templateId}
                    setTemplateId={setTemplateId} templates={templates} />
      )}

      {tab === "scheduled" && (
        <ScheduledView reports={reports} onEdit={(r) => setEditingReport(r)}
                       onDelete={deleteReport} onRunNow={runNow} />
      )}

      {tab === "generated" && (
        <GeneratedView instances={instances} onOpenMeeting={openInstanceAsMeeting} onDownload={downloadPdf} />
      )}

      {tab === "meetings" && (
        <MeetingsView meetings={meetings} />
      )}

      <ReportEditor open={editingReport !== null} onClose={() => setEditingReport(null)}
                    initial={editingReport} templates={templates} onSaved={() => { setEditingReport(null); loadCore(); }} />
    </div>
  );
}

function TrendsView({ trends, days, setDays, templateId, setTemplateId, templates }) {
  if (!trends) return <div className="font-mono text-[#1a1a1a]/60">Loading trends…</div>;
  const score = trends.trend_score;
  const tone = score >= 80 ? "#1a8754" : score >= 55 ? "#d6a900" : "#ff4500";
  const topFails = (trends.top_fail_items || []).slice(0, 8);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Filter</div>
        <select data-testid="trends-days" value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-xs uppercase tracking-wider font-bold text-[#1a1a1a]">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <select data-testid="trends-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}
          className="bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-xs uppercase tracking-wider font-bold text-[#1a1a1a]">
          <option value="">All checklists</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="surface-card rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-15" style={{ background: tone }} />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Trend score</div>
          <div className="font-display font-bold text-base">Period score</div>
          <div className="mt-5 flex items-end gap-2">
            <div className="font-display font-black text-6xl leading-none" style={{ color: tone }}>{score}</div>
            <div className="text-sm text-[#1a1a1a]/65 mb-2">/ 100</div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-[#fff1ea] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, score)}%`, background: tone }} />
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Kv label="Total runs" v={trends.total_runs} />
            <Kv label="Items inspected" v={trends.total_items} />
            <Kv label="Failures" v={trends.total_fails} accent />
            <Kv label="Fail rate" v={`${trends.fail_rate_pct}%`} accent />
          </div>
        </div>

        <div className="lg:col-span-2 surface-card rounded-2xl p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Daily fail rate</div>
          <div className="font-display font-bold text-base">Compliance trend</div>
          <div className="mt-3" style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer minWidth={0} minHeight={0}>
              <AreaChart data={trends.series}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SCARLET} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={SCARLET} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,139,0.08)" />
                <XAxis dataKey="date" stroke={NAVY} fontSize={10} tickFormatter={(d) => (d || "").slice(5)} />
                <YAxis stroke={NAVY} fontSize={10} unit="%" />
                <Tooltip contentStyle={{ background: "white", border: `1px solid ${NAVY}`, borderRadius: 8 }} />
                <Area type="monotone" dataKey="fail_rate_pct" stroke={SCARLET} strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="surface-card rounded-2xl p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Top failing items</div>
          <div className="font-display font-bold text-base mb-3">Where the floor is slipping</div>
          {topFails.length === 0 && <Empty msg="No failures in this period" />}
          <ul className="space-y-2">
            {topFails.map((it) => (
              <li key={it.title} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#fff1ee] flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-[#ff4500]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">{it.title}</div>
                  <div className="h-1.5 mt-1 bg-[#fff1ea] rounded-full overflow-hidden">
                    <div className="h-full bg-[#ff4500]" style={{ width: `${Math.min(100, (it.count / (topFails[0]?.count || 1)) * 100)}%` }} />
                  </div>
                </div>
                <div className="font-display font-black text-[#ff4500] text-lg">{it.count}</div>
              </li>
            ))}
          </ul>
        </div>
        <div className="surface-card rounded-2xl p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// By template</div>
          <div className="font-display font-bold text-base">Audit breakdown</div>
          <div className="mt-3" style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer minWidth={0} minHeight={0}>
              <BarChart data={trends.by_template}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,139,0.08)" />
                <XAxis dataKey="template" stroke={NAVY} fontSize={9} />
                <YAxis stroke={NAVY} fontSize={10} unit="%" />
                <Tooltip contentStyle={{ background: "white", border: `1px solid ${NAVY}`, borderRadius: 8 }} />
                <Bar dataKey="fail_rate_pct" fill={SCARLET} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScheduledView({ reports, onEdit, onDelete, onRunNow }) {
  if (reports.length === 0)
    return <Empty msg='No scheduled reports. Click "Schedule report" to create one.' />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {reports.map((r) => (
        <div key={r.id} className="surface-card rounded-2xl p-5" data-testid={`report-card-${r.id}`}>
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 bg-[#fff1ea] rounded-full px-2.5 py-1">
              <CalendarClock className="w-3 h-3 text-[#1a1a1a]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]">{r.frequency}</span>
            </div>
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${r.auto_send ? "bg-[#1a8754]/10 text-[#1a8754]" : "bg-[#1a1a1a]/10 text-[#1a1a1a]"}`}>
              {r.auto_send ? "auto" : "manual"}
            </span>
          </div>
          <div className="mt-3 font-display font-black text-lg leading-tight">{r.title}</div>
          {r.description && <div className="text-[11px] text-[#1a1a1a]/65 mt-1 line-clamp-2">{r.description}</div>}
          <div className="mt-3 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/65">
            <Clock className="w-3 h-3" /> Next: {(r.next_run_at || "").slice(0, 10)} · {String(r.schedule_hour || 9).padStart(2, "0")}:00
          </div>
          <div className="mt-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500]">Recipients</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {(r.recipients || []).length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">—</span>}
              {(r.recipients || []).map((rc, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-[#f7f5f2] border border-[#1a1a1a]/10 rounded-full px-2.5 py-1 text-[10px]">
                  <Mail className="w-2.5 h-2.5 text-[#1a1a1a]" /> {rc.email}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button data-testid={`run-now-${r.id}`} onClick={() => onRunNow(r.id)} className="btn-pill btn-pill-primary">
              <Play className="w-3 h-3" /> Run now
            </button>
            <button data-testid={`edit-report-${r.id}`} onClick={() => onEdit(r)} className="btn-pill btn-pill-secondary">Edit</button>
            <button data-testid={`delete-report-${r.id}`} onClick={() => onDelete(r.id)} className="text-[#ff4500] hover:bg-[#fff1ee] p-2 rounded-full">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function GeneratedView({ instances, onOpenMeeting, onDownload }) {
  if (instances.length === 0)
    return <Empty msg="No generated reports yet. Run a scheduled report or wait for the next auto-cycle." />;
  return (
    <div className="space-y-3">
      {instances.map((i) => (
        <div key={i.id} className="surface-card rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-testid={`instance-${i.id}`}>
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-[#fff1ee] flex items-center justify-center">
              <FileDown className="w-5 h-5 text-[#ff4500]" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-lg truncate">{i.title}</div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">{i.label}</div>
              <div className="text-[11px] mt-1 text-[#1a1a1a]/70">
                Trend <b className="text-[#1a1a1a]">{i.data?.trend_score}/100</b> · {i.data?.total_runs} runs · {i.data?.total_fails} fails · fail-rate {i.data?.fail_rate_pct}%
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid={`download-instance-${i.id}`} onClick={() => onDownload(i.pdf_file_id)} className="btn-pill btn-pill-secondary">
              <FileDown className="w-3.5 h-3.5" /> PDF
            </button>
            <button data-testid={`open-meeting-${i.id}`} onClick={() => onOpenMeeting(i)} className="btn-pill btn-pill-primary">
              Open meeting <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function MeetingsView({ meetings }) {
  if (meetings.length === 0)
    return <Empty msg="No meetings yet. Open a generated report → 'Open meeting' to start one." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {meetings.map((m) => (
        <Link key={m.id} to={`/app/close-out-meeting/${m.id}`} data-testid={`meeting-${m.id}`} className="surface-card rounded-2xl p-5 hover:shadow-lift transition-all duration-150 hover:-translate-y-0.5">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 bg-[#fff1ea] rounded-full px-2.5 py-1">
              <MessageSquareText className="w-3 h-3 text-[#1a1a1a]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]">{m.status}</span>
            </div>
            <span className="text-[10px] font-mono text-[#1a1a1a]/55">{new Date(m.created_at).toLocaleDateString()}</span>
          </div>
          <div className="mt-3 font-display font-black text-base leading-tight">{m.title}</div>
          <div className="text-[11px] text-[#1a1a1a]/65 mt-1">{(m.attendees || []).length} attendee(s) · {(m.agenda || []).length} agenda items</div>
        </Link>
      ))}
    </div>
  );
}

function Kv({ label, v, accent }) {
  return (
    <div className="bg-[#f7f5f2] rounded-xl px-3 py-2">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/55">{label}</div>
      <div className={`font-display font-bold text-lg ${accent ? "text-[#ff4500]" : "text-[#1a1a1a]"}`}>{v}</div>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="py-12 text-center bg-white rounded-2xl border border-dashed border-[#1a1a1a]/15">
      <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{msg}</div>
    </div>
  );
}

function ReportEditor({ open, onClose, initial, templates, onSaved }) {
  const [form, setForm] = useState({
    title: "", description: "", frequency: "weekly",
    template_ids: [], team_ids: [], recipients: [],
    schedule_hour: 8, auto_send: true,
  });
  const [recip, setRecip] = useState({ name: "", email: "", role: "" });

  useEffect(() => {
    if (initial && initial.id) {
      setForm({
        title: initial.title || "", description: initial.description || "",
        frequency: initial.frequency || "weekly",
        template_ids: initial.template_ids || [],
        team_ids: initial.team_ids || [],
        recipients: initial.recipients || [],
        schedule_hour: initial.schedule_hour ?? 8,
        auto_send: initial.auto_send !== false,
      });
    } else if (initial) {
      setForm({ title: "", description: "", frequency: "weekly",
        template_ids: [], team_ids: [], recipients: [],
        schedule_hour: 8, auto_send: true });
    }
  }, [initial]);

  const addRecip = () => {
    if (!recip.email) return toast.error("Email required");
    setForm({ ...form, recipients: [...form.recipients, recip] });
    setRecip({ name: "", email: "", role: "" });
  };

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    if (initial?.id) {
      await api.patch(`/reports/${initial.id}`, form);
      toast.success("Updated");
    } else {
      await api.post("/reports", form);
      toast.success("Scheduled");
    }
    onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl border-[#1a1a1a]/15">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl">{initial?.id ? "Edit report" : "Schedule a report"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-auto scrollbar-thin pr-1">
          <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} tid="report-title-input" placeholder="Weekly Compliance Digest" />
          <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} tid="report-desc-input" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Frequency</Label>
              <select data-testid="report-frequency" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
            <div>
              <Label>Send hour</Label>
              <select data-testid="report-hour" value={form.schedule_hour} onChange={(e) => setForm({ ...form, schedule_hour: Number(e.target.value) })}
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                {[6,7,8,9,10,11,12,13,14,15,16,17].map((h) => <option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>)}
              </select>
            </div>
            <div>
              <Label>Auto send</Label>
              <label className="inline-flex items-center gap-2 text-sm h-[42px]">
                <input data-testid="report-autosend" type="checkbox" checked={form.auto_send} onChange={(e) => setForm({ ...form, auto_send: e.target.checked })} className="accent-[#ff4500]" />
                Generate + email automatically
              </label>
            </div>
          </div>
          <div>
            <Label>Limit to specific checklists (optional)</Label>
            <div className="flex flex-wrap gap-1.5">
              {templates.map((t) => {
                const on = form.template_ids.includes(t.id);
                return (
                  <button key={t.id} data-testid={`report-template-${t.id}`} type="button"
                    onClick={() => setForm({
                      ...form,
                      template_ids: on ? form.template_ids.filter((x) => x !== t.id) : [...form.template_ids, t.id]
                    })}
                    className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20"}`}>
                    {t.name}
                  </button>
                );
              })}
              {templates.length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">No templates yet</span>}
            </div>
          </div>
          <div>
            <Label>Recipients</Label>
            <div className="grid grid-cols-3 gap-2">
              <input data-testid="recipient-name" placeholder="Name" value={recip.name} onChange={(e) => setRecip({ ...recip, name: e.target.value })} className="bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
              <input data-testid="recipient-email" placeholder="Email" value={recip.email} onChange={(e) => setRecip({ ...recip, email: e.target.value })} className="bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <input data-testid="recipient-role" placeholder="Role" value={recip.role} onChange={(e) => setRecip({ ...recip, role: e.target.value })} className="flex-1 bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
                <button data-testid="add-recipient" type="button" onClick={addRecip} className="btn-pill btn-pill-primary">Add</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {form.recipients.map((r, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-[#f7f5f2] border border-[#1a1a1a]/10 rounded-full px-2.5 py-1 text-[11px]">
                  <Mail className="w-2.5 h-2.5 text-[#1a1a1a]" />
                  {r.email} {r.role && <span className="text-[#1a1a1a]/55">· {r.role}</span>}
                  <button onClick={() => setForm({ ...form, recipients: form.recipients.filter((_, idx) => idx !== i) })}>
                    <X className="w-2.5 h-2.5 text-[#ff4500]" />
                  </button>
                </span>
              ))}
            </div>
            <div className="mt-2 text-[10px] font-mono text-[#ff4500] uppercase tracking-wider">
              ⚠ Email delivery is MOCKED for v1. PDFs generate for real and are openable in-app as meetings.
            </div>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="save-report-button" onClick={save} className="btn-pill btn-pill-primary">
            {initial?.id ? "Save changes" : "Schedule"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, placeholder, tid }) {
  return (
    <div>
      <Label>{label}</Label>
      <input data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a] focus:ring-2 focus:ring-[#1a1a1a]/15" />
    </div>
  );
}

function Label({ children }) {
  return <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1.5 font-mono">{children}</label>;
}
